import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import type { PaymentBreakdown } from '@/api/reports-extra';
import { money } from '@/lib/utils';

const COLORS: Record<string, string> = {
  Cash: '#12B76A',
  Upi: '#8B5CF6',
  Card: '#FFA53D',
  Credit: '#2FB4E8',
  Online: '#FF6B4D',
  Other: '#B8AC9C',
};

export function PaymentBreakdownChart({ data }: { data: PaymentBreakdown[] }) {
  if (data.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
        No sales in this period.
      </div>
    );
  }

  const total = data.reduce((sum, d) => sum + d.total, 0);

  return (
    <div className="flex items-center gap-4">
      <ResponsiveContainer width={140} height={140}>
        <PieChart>
          <Pie data={data} dataKey="total" nameKey="mode" innerRadius={40} outerRadius={65} paddingAngle={2}>
            {data.map((entry) => (
              <Cell key={entry.mode} fill={COLORS[entry.mode] ?? COLORS.Other} />
            ))}
          </Pie>
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null;
              const entry = payload[0].payload as PaymentBreakdown;
              return (
                <div className="rounded-md border border-border bg-popover px-2.5 py-1.5 text-xs shadow-lg">
                  {entry.mode}: {money(entry.total)}
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>

      <div className="flex-1 space-y-1.5">
        {data
          .slice()
          .sort((a, b) => b.total - a.total)
          .map((entry) => (
            <div key={entry.mode} className="flex items-center justify-between gap-2 text-xs">
              <span className="flex items-center gap-1.5">
                <span
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: COLORS[entry.mode] ?? COLORS.Other }}
                />
                {entry.mode}
              </span>
              <span className="tabular text-muted-foreground">
                {total > 0 ? Math.round((entry.total / total) * 100) : 0}%
              </span>
            </div>
          ))}
      </div>
    </div>
  );
}